import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { getDB, EntityStore, isCacheUnavailableError } from './db';
import { enqueueMutation } from './sync-queue';
import { useSyncStore } from './sync-store';
import { unwrap } from '../api-envelope';

/**
 * useOfflineQuery — fetch from network when online, fall back to IndexedDB when offline.
 */
export function useOfflineQuery<T = any>(
  entity: EntityStore,
  queryKey: string[],
  networkFetcher: () => Promise<T[]>,
  options?: {
    enabled?: boolean;
    filterFn?: (item: any) => boolean;
    /**
     * Poll interval, in ms, or false for no polling (the default).
     *
     * Only ever passed while something on screen depends on the list MOVING —
     * a ticket open in the editor, whose "edited by X, N minutes ago" stamp is
     * the only signal a hand gets that the row changed under them. A list that
     * nothing is watching must not poll: this runs on phones, in trucks, on one
     * bar of LTE.
     */
    refetchInterval?: number | false;
  }
) {
  const isOnline = useSyncStore((s) => s.isOnline);

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (isOnline) {
        let data: T[] | undefined;
        try {
          data = await networkFetcher();
        } catch (err) {
          // Network error — fall through to IndexedDB
          console.warn(`[offline] Network fetch failed for ${entity}, using cache:`, err);
        }

        if (data !== undefined) {
          // Replace IndexedDB cache with server data (removes stale/ghost entries).
          // Best-effort: the server data is already in hand, so a cache that is
          // unwritable or sealed by the tenant-purge quarantine must not cause
          // us to discard it and fall back to reading that same cache.
          try {
            const db = await getDB();
            const tx = db.transaction(entity, 'readwrite');
            await tx.store.clear();
            for (const item of data as any[]) {
              await tx.store.put(item);
            }
            await tx.done;
          } catch (err) {
            console.warn(`[offline] Cache write skipped for ${entity}:`, err);
          }
          return data;
        }
      }

      // Offline or network error — read from IndexedDB.
      //
      // A sealed or unopenable cache resolves to an empty list rather than
      // rejecting. Rejecting is not free here: React Query would retry once,
      // so the screen sits on `isLoading` for the open budget *twice* plus the
      // backoff before it renders anything, and what it renders in the end is
      // the same empty list. An empty list plus the amber "offline storage
      // unavailable" banner tells the truth — there is nothing cached — and it
      // tells it immediately instead of after a spinner the user reads as a
      // hung app. The banner is the part that stops this looking like "you
      // have no tickets".
      let db;
      try {
        db = await getDB();
      } catch (err) {
        if (isCacheUnavailableError(err)) {
          console.warn(`[offline] Cache unavailable for ${entity}; rendering empty.`);
          return [] as unknown as T[];
        }
        throw err;
      }

      let items = await db.getAll(entity);
      if (options?.filterFn) {
        items = items.filter(options.filterFn);
      }
      return items as unknown as T[];
    },
    staleTime: isOnline ? 30_000 : Infinity,
    // Never poll offline — there is nothing to poll, and the queryFn would
    // simply re-read IndexedDB on a timer.
    refetchInterval: isOnline ? (options?.refetchInterval ?? false) : false,
    enabled: options?.enabled !== false,
  });
}

/**
 * useOfflineMutation — write to API when online, queue to IndexedDB when offline.
 */
export function useOfflineMutation(
  entity: EntityStore,
  action: 'create' | 'update' | 'delete',
  onlineMutator: (vars: any) => Promise<any>,
  options?: {
    invalidateKeys?: string[][];
    onSuccess?: (data: any, vars: any) => void;
    onError?: (error: any, vars: any) => void;
  }
) {
  const isOnline = useSyncStore((s) => s.isOnline);
  const queryClient = useQueryClient();

  return useMutation({
    /*
     * The offline branch below is only reachable because of this line.
     *
     * React Query's default `networkMode` is 'online', and it makes its own
     * decision about connectivity from `onlineManager` (navigator.onLine plus
     * the window online/offline events) BEFORE it ever calls `mutationFn`.
     * With the default, a mutation fired in a dead zone is put in the `paused`
     * state and the function below never runs: nothing is written to
     * IndexedDB, nothing is enqueued, `onError` never fires, and the button
     * sits disabled with `isPending` true forever. On a reload the hand's work
     * is gone, and the app never said a word.
     *
     * 'always' means the mutation always executes and the online/offline
     * decision belongs to `isOnline` in the body below — which is this app's
     * own detector (see lib/offline/online-detector), the one the sync bar and
     * the screens' copy already agree with. One connectivity opinion, not two.
     *
     * Scoped deliberately to this hook rather than the QueryClient default:
     * the other 264 useMutation call sites have no offline path, and pausing
     * is the correct behaviour for a write with nowhere to queue.
     */
    networkMode: 'always',
    mutationFn: async (vars: any) => {
      if (isOnline) {
        try {
          const result = await onlineMutator(vars);

          // The server has already accepted this write. Mirroring it into the
          // cache is a nicety; failing to is not a failed save. Letting a
          // cache error escape here would surface "Failed to save ticket" for
          // a ticket that saved fine, and the field hand's reasonable response
          // to that is to submit it again.
          try {
            const db = await getDB();
            // Every `onlineMutator` passed to this hook returns an axios
            // response, so the record lives in the envelope at `result.data`.
            // The old `|| result` arm only ever fired when the unwrap had
            // already fallen through, and it handed the axios response itself
            // to `db.put` — a wrapper object, not a record. A server that
            // answers `{ success: true }` with no payload (the delete
            // handlers do) must reach `record` as null and skip the mirror,
            // which is what the `record?.id` guard below then does.
            const record = unwrap<{ id?: string }>(result?.data);
            if (record?.id) {
              if (action === 'delete') {
                await db.delete(entity, record.id);
              } else {
                await db.put(entity, record);
              }
            }
          } catch (cacheErr) {
            console.warn(`[offline] Cache update skipped for ${entity}:`, cacheErr);
          }

          return result;
        } catch (err: any) {
          // If network error, fall through to offline queue
          if (!err.response) {
            console.warn(`[offline] Mutation failed, queueing offline:`, err);
          } else {
            throw err; // Server error — don't queue
          }
        }
      }

      // Offline — write optimistically to IndexedDB and queue.
      //
      // This one must fail loudly. The offline queue IS the save: there is no
      // server copy and no retry behind it, so a swallowed error here is a
      // ticket the user watched "save" that does not exist anywhere. Rethrown
      // with a message the screens' existing onError handlers can show
      // verbatim, because the generic "Failed to save ticket" would leave the
      // user believing a retry might work when nothing will until they are
      // back on connection.
      let db;
      try {
        db = await getDB();
      } catch (err) {
        if (isCacheUnavailableError(err)) {
          throw new Error(
            'Offline storage is unavailable — this cannot be saved until you are back online.'
          );
        }
        throw err;
      }

      const id = vars.id || uuidv4();
      const version = vars.version;

      if (action === 'create') {
        const record = {
          ...vars.data,
          id,
          _pendingSync: true,
          status: vars.data?.status || 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        // Use placeholder numbers for tickets/jobs
        if (entity === 'tickets' && !record.ticket_number) {
          record.ticket_number = `DRAFT-${id.slice(0, 5)}`;
        }
        if (entity === 'jobs' && !record.job_number) {
          record.job_number = `DRAFT-${id.slice(0, 5)}`;
        }
        await db.put(entity, record);
        await enqueueMutation(entity as any, 'create', id, vars.data);
        return { data: { data: record } };
      }

      if (action === 'update') {
        const existing = await db.get(entity, id);
        if (existing) {
          const updated = {
            ...existing,
            ...vars.data,
            _pendingSync: true,
            updated_at: new Date().toISOString(),
          };
          await db.put(entity, updated);
        }
        await enqueueMutation(entity as any, 'update', id, vars.data, version);
        return { data: { data: { ...existing, ...vars.data } } };
      }

      if (action === 'delete') {
        await db.delete(entity, id);
        await enqueueMutation(entity as any, 'delete', id, {});
        return { data: { success: true } };
      }
    },
    onSuccess: (data, vars) => {
      // Invalidate queries
      for (const key of options?.invalidateKeys || []) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      options?.onSuccess?.(data, vars);
    },
    onError: options?.onError,
  });
}
